/**
 * Wrapper component for the HTML5 video player. Consumes a Backbone model and
 * passes along relevant attributes.
 *
 * WARNING(kevinb): `model` is typed as `any`.
 */
import * as React from "react";

import {staticUrl} from "@khan/urls";

import {VideoPlayerState} from "../constants.ts";
import {
    getPlaybackRateEnum,
    getPlaybackRateNumber,
} from "../playback-rate-helpers.ts";
import type {SharedProps, VideoPlayerStateType} from "../types.ts";

import HtmlPlayer from "./html-player.tsx";

// Attributes from the Backbone VideoPlayerModel that this component relies on.
// Keep this list and the SharedProps type in sync.
const modelAttributes = ["downloadUrls"];

export default class HtmlPlayerWrapper extends React.Component<SharedProps> {
    _playerNode: React.ElementRef<typeof HtmlPlayer> | null | undefined;

    componentDidMount() {
        const {model} = this.props;

        // HACK(riley): This is a holdover from the transition away from our
        //              legacy package. Eventually we'll want to set & handle
        //              this further up the tree.
        model.player = this.getPublicAPI();

        // Listen for change events on the backbone model for the specific
        // attributes that we're interested in for the video player.
        modelAttributes
            .map((key) => `change:${key}`)
            .forEach((change) =>
                model.on(change, this.forceUpdateIgnoreArgs, this),
            );
    }

    componentDidUpdate(prevProps: SharedProps) {
        // If the enum-based prop changes (e.g., from settings load), sync model rate.
        if (prevProps.playbackRate !== this.props.playbackRate) {
            const playbackRateNumber = getPlaybackRateNumber(
                this.props.playbackRate,
            );
            this.props.model.setPlaybackRate(playbackRateNumber);
        }
    }

    componentWillUnmount() {
        const {model} = this.props;

        // NOTE(jeresig): We need to pause the video when the user navigates
        // away to ensure that we don't try to keep logging progress on a
        // video that no longer exists.
        model.setPlayerState(VideoPlayerState.PAUSED);

        // We can't use the unspecified 'change' string since we added specific
        // keys above, so we pass `null` for the event and specify the
        // listeners to remove by their callback.
        model.off(null, this.forceUpdateIgnoreArgs);
    }

    // HACK: Backbone sends an argument into forceUpdate on change that causes
    //       React to complain. We can't just pass an anonymous function to the
    //       listener since we need to remove it with .off later. So, we pull
    //       this maneuver.
    forceUpdateIgnoreArgs: () => void = () => {
        this.forceUpdate();
    };

    // Public methods to match the YouTube player's API.
    getPublicAPI: () => {
        getCurrentTime: () => number;
        getDuration: () => number;
        getPlaybackRate: () => any;
        getPlayerState: () => any;
        playVideo: () => void;
        seekTo: (seconds: number) => void;
    } = () => {
        const {model} = this.props;

        return {
            getCurrentTime: () => 0,
            getDuration: () => 0,
            getPlaybackRate: () => model.get("playbackRate"),
            getPlayerState: () => model.get("playerState"),
            playVideo: this._playerNode?.play || (() => {}),
            seekTo: this._playerNode?.seekTo || (() => {}),

            // TODO(riley): Verify that these are all still needed in the new
            //              player. Also, look into:
            //
            // getOption: () => {},
            // getOptions: () => {},
        };
    };

    handleStateChange: (newState: VideoPlayerStateType) => void = (
        newState: VideoPlayerStateType,
    ) => {
        const {model, onStateChange} = this.props;

        model.setPlayerState(newState);
        onStateChange({data: newState});
    };

    onDurationChange: (duration: number) => void = (duration: number): void => {
        const {model} = this.props;

        if (model && model.player) {
            // NOTE (jsatk): This is a slightly less-gross solution to a
            // previously grosser hack.
            model.player.getDuration = () => duration;
        }
    };

    onTimeUpdate: (currentTime: number) => void = (
        currentTime: number,
    ): void => {
        const {model} = this.props;

        if (model && model.player) {
            // NOTE (jsatk): This is a slightly less-gross solution to a
            // previously grosser hack.
            model.player.getCurrentTime = () => currentTime;
        }
    };

    render(): React.ReactNode {
        const {
            autoplay,
            model,
            onPlayerReady,
            startTime,
            styles,
            tabAccessible,
            title,
            muteVideos,
            playbackRate,
            showCaptions,
            updateMuteVideosSetting,
            updatePlaybackRateSetting,
            updateShowCaptionsSetting,
        } = this.props;

        const downloadUrls = model.get("downloadUrls") || {};
        const translatedYoutubeId: string | null | undefined = model.get(
            "translatedYoutubeId",
        );

        // Prefer the model as source of truth for numeric rate; fall back to prop enum.
        const playbackRateNumber =
            (model.get && model.get("playbackRate")) ||
            getPlaybackRateNumber(playbackRate);

        return (
            <HtmlPlayer
                autoplay={autoplay}
                muteVideos={muteVideos}
                showCaptions={showCaptions}
                onDurationChange={this.onDurationChange}
                onPlayerReady={onPlayerReady}
                onStateChange={this.handleStateChange}
                onTimeUpdate={this.onTimeUpdate}
                playbackRateNumber={playbackRateNumber}
                onPlaybackRateChange={(rateNumber) => {
                    const enumRate = getPlaybackRateEnum(rateNumber);
                    if (enumRate != null) {
                        const numeric = getPlaybackRateNumber(enumRate);
                        model.setPlaybackRate(numeric);
                        updatePlaybackRateSetting(enumRate);
                    }
                }}
                poster={staticUrl(downloadUrls.png)}
                ref={(node) => (this._playerNode = node)}
                src={staticUrl(downloadUrls.mp4)}
                translatedYoutubeId={translatedYoutubeId}
                startTime={startTime}
                styles={styles}
                tabAccessible={tabAccessible}
                title={title}
                updateMuteVideosSetting={updateMuteVideosSetting}
                updateShowCaptionsSetting={updateShowCaptionsSetting}
            />
        );
    }
}
